import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../Company/About/Navbar";
import { useAuth } from "../../context/AuthContext";
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Lock, Loader2, CheckCircle2, AlertTriangle, Eye, EyeOff, ArrowLeft,
} from "lucide-react";

/**
 * Account security — lets a signed-in candidate CREATE a password (if they
 * signed up with Google/LinkedIn and have none yet) or CHANGE an existing one,
 * so they can log in with email + password in addition to social sign-in.
 *
 * Backend (session-authenticated, same cookie as the rest of the user app):
 *   GET  /api/user/password-status  -> { has_password }
 *   POST /api/user/set-password     -> { new_password, confirm_password, current_password? }
 */
export default function AccountSecurity() {
  const navigate = useNavigate();
  const { userEmail } = useAuth();

  const [loading, setLoading] = useState(true);
  const [hasPassword, setHasPassword] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { type: "ok" | "err", text }

  // Ask the backend whether this account already has a local password, so we
  // show "Create" (no current-password field) vs "Change" (requires current).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/user/password-status", {
          method: "GET",
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setHasPassword(Boolean(data.has_password));
        }
      } catch {
        /* leave default (create mode) — the backend still enforces the rule */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (next !== confirm) {
      setMsg({ type: "err", text: "New passwords do not match." });
      return;
    }
    setBusy(true);
    try {
      const res = await fetch("/api/user/set-password", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          new_password: next,
          confirm_password: confirm,
          ...(hasPassword ? { current_password: current } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMsg({ type: "ok", text: data.message || "Password saved." });
        setCurrent(""); setNext(""); setConfirm("");
        setHasPassword(true); // now they have one → switch to change mode
      } else {
        setMsg({
          type: "err",
          text: data.error || "Could not save your password. Please try again.",
        });
      }
    } catch {
      setMsg({ type: "err", text: "Network error. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-muted/30 flex flex-col">
      <Navbar />
      <main className="flex-1 py-10 px-4">
        <div className="max-w-lg mx-auto">
          <button
            onClick={() => navigate("/user-home")}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </button>

          <Card>
            <CardHeader>
              <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-2">
                <Lock className="h-5 w-5 text-primary" />
              </div>
              <CardTitle>{hasPassword ? "Change password" : "Create a password"}</CardTitle>
              <CardDescription>
                {hasPassword
                  ? "Update the password you use to sign in with your email."
                  : "Set a password so you can sign in with your email — not just Google. You can keep using Google too."}
              </CardDescription>
              {userEmail ? (
                <div className="text-xs text-muted-foreground pt-1">
                  Signed in as {userEmail}
                </div>
              ) : null}
            </CardHeader>

            <form onSubmit={submit}>
              <CardContent className="space-y-4">
                {loading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                  </div>
                ) : (
                  <>
                    {hasPassword && (
                      <div className="space-y-1.5">
                        <Label htmlFor="current">Current password</Label>
                        <Input
                          id="current"
                          type="password"
                          autoComplete="current-password"
                          value={current}
                          onChange={(e) => setCurrent(e.target.value)}
                          required
                        />
                      </div>
                    )}

                    <div className="space-y-1.5">
                      <Label htmlFor="next">New password</Label>
                      <div className="relative">
                        <Input
                          id="next"
                          type={showNew ? "text" : "password"}
                          autoComplete="new-password"
                          value={next}
                          onChange={(e) => setNext(e.target.value)}
                          required
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNew((s) => !s)}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          aria-label={showNew ? "Hide password" : "Show password"}
                        >
                          {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        At least 8 characters, with both letters and numbers.
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="confirm">Confirm new password</Label>
                      <Input
                        id="confirm"
                        type={showNew ? "text" : "password"}
                        autoComplete="new-password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        required
                      />
                    </div>

                    {msg && (
                      <Alert
                        variant={msg.type === "err" ? "destructive" : "default"}
                        className={msg.type === "ok" ? "border-primary/40 text-primary" : ""}
                      >
                        {msg.type === "ok" ? (
                          <CheckCircle2 className="h-4 w-4" />
                        ) : (
                          <AlertTriangle className="h-4 w-4" />
                        )}
                        <AlertDescription className={msg.type === "ok" ? "text-primary" : ""}>
                          {msg.text}
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                )}
              </CardContent>

              <CardFooter>
                <Button type="submit" disabled={busy || loading} className="w-full">
                  {busy ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</>
                  ) : hasPassword ? (
                    "Update password"
                  ) : (
                    "Create password"
                  )}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
      </main>
    </div>
  );
}
